import i18next from "i18next";
import { capitalize } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { AndroidVersionHeader } from "src/ui/shared/table/headers/AndroidVersionHeader";
import { ChannelHeader } from "src/ui/shared/table/headers/ChannelHeader";
import { CodenameHeader } from "src/ui/shared/table/headers/CodenameHeader";
import { ProductNameHeader } from "src/ui/shared/table/headers/ProductNameHeader";
import { VisibilityHeader } from "src/ui/shared/table/headers/VisibilityHeader";

import { columnFilter } from "@dashboard/devices/components/Table/filters/DeviceTableFilters.utils";
import { ReleaseFile } from "@dashboard/downloads/types/index";
import { Paper, TableContainer, TableRow, Typography } from "@mui/material";
import SemverService from "@services/semverService/semverService";
import TimeService from "@services/timeService/timeService";
import UserSettingsService from "@services/userSettingsService";
import { MinimalTable, MinimalTableCell, MinimalTableHeader, MinimalTableHeaderCell } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { fuzzyFilter } from "@shared/table/Tanstack.utils";
import { Tooltip } from "@shared/Tooltip";
import {
    ColumnDef,
    ColumnFiltersState,
    functionalUpdate,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

import TableItem from "./TableItem";

type Props = {
    data: ReleaseFile[];
};

const formatAndroidVersion = (version: string) => {
    const major = SemverService.parse(version)?.major;
    return major !== undefined ? i18next.t("common:label.androidWithValue", { version: major }) : "";
};

const tableColumns: ColumnDef<ReleaseFile, string>[] = [
    {
        header: i18next.t("common:label.date"),
        accessorKey: "created",
        accessorFn: (row) => row.created,
        cell: (info) => (
            <MinimalTableCell title="">
                <Tooltip title={TimeService.getLocalDate(info.getValue(), "ISO", "dateTime")}>
                    <Typography>{TimeService.getLocalDate(info.getValue(), "ISO", "date")}</Typography>
                </Tooltip>
            </MinimalTableCell>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            const timeA = new Date(rowA.original.created).getTime();
            const timeB = new Date(rowB.original.created).getTime();
            return timeA - timeB;
        },
    },
    {
        header: i18next.t("common:label.productName"),
        accessorKey: "productName",
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 300 }}>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
        filterFn: columnFilter,
    },
    {
        header: i18next.t("common:label.os"),
        accessorKey: "androidVersion",
        accessorFn: (row) => formatAndroidVersion(row.version),
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 140 }}>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        filterFn: columnFilter,
    },
    {
        header: i18next.t("common:label.version"),
        accessorKey: "version",
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 100 }}>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => SemverService.compare(rowA.original.version, rowB.original.version),
    },
    {
        header: i18next.t("common:label.channel"),
        accessorKey: "channel",
        accessorFn: (row) => capitalize(row.channel ?? "none"),
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 100 }}>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        filterFn: columnFilter,
    },
    {
        header: i18next.t("common:label.visibility"),
        accessorKey: "visibility",
        accessorFn: (row) => (row.isPublic ? "Public" : "Private"),
        cell: (info) => (
            <MinimalTableCell style={{ maxWidth: 100 }}>
                <Typography>
                    {info.getValue() === "Public"
                        ? i18next.t("pages:downloadsPage.table.visibility.public")
                        : i18next.t("pages:downloadsPage.table.visibility.private")}
                </Typography>
            </MinimalTableCell>
        ),
        filterFn: columnFilter,
    },
];

const DownloadsTable = (props: Props) => {
    const { t } = useTranslation(["pages", "common"]);

    const [sorting, setSorting] = React.useState(UserSettingsService.getDownloadsPageSorting());
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
        ...UserSettingsService.getDownloadsPageColumnFilters(),
    ]);

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("downloadsPage.columnFilters", columnFilters);
    }, [columnFilters]);

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting);
        setSorting(newValue);
        UserSettingsService.setWorkspaceValue("downloadsPage.sorting", newValue);
    };

    const table = useReactTable<ReleaseFile>({
        data: props.data,
        columns: tableColumns,
        filterFns: { fuzzyFilter },
        state: { columnFilters, sorting },
        initialState: {
            pagination: {
                pageSize: UserSettingsService.getDownloadsPageRowsPerPage(),
                pageIndex: 0,
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        table.setPageIndex(0);
    };

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <React.Fragment>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header}>
                                        {header.column.id === "codename" && (
                                            <CodenameHeader
                                                codenames={props.data.map((d) => d.codename)}
                                                columnFilters={columnFilters}
                                                setColumnFilters={setColumnFilters}
                                            />
                                        )}
                                        {header.column.id === "productName" && (
                                            <ProductNameHeader
                                                productNames={props.data.map((d) => d.productName)}
                                                columnFilters={columnFilters}
                                                setColumnFilters={setColumnFilters}
                                            />
                                        )}
                                        {header.column.id === "channel" && (
                                            <ChannelHeader columnFilters={columnFilters} setColumnFilters={setColumnFilters} />
                                        )}
                                        {header.column.id === "visibility" && (
                                            <VisibilityHeader columnFilters={columnFilters} setColumnFilters={setColumnFilters} />
                                        )}
                                        {header.column.id === "androidVersion" && (
                                            <AndroidVersionHeader
                                                androidVersions={props.data.map((d) => formatAndroidVersion(d.version))}
                                                columnFilters={columnFilters}
                                                setColumnFilters={setColumnFilters}
                                            />
                                        )}
                                    </Header>
                                ))}
                                <MinimalTableHeaderCell></MinimalTableHeaderCell>
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody<ReleaseFile>
                        table={table}
                        filter={{ isActive: !!columnFilters.length, onResetFilter: handleResetFilter }}
                        noDataMessage={t("pages:downloadsPage.table.noResults")}
                    >
                        {table.getRowModel().rows.map((row, index) => (
                            <TableItem key={row.id} row={row} index={index} />
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                component="div"
                rowsPerPageOptions={[12, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 12;
                    table.setPageSize(size);
                    UserSettingsService.setWorkspaceValue("downloadsPage.rowsPerPage", size);
                }}
            />
        </React.Fragment>
    );
};

export default DownloadsTable;
